import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { NextPage, NextPageContext } from 'next';

import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

import { useTranslations } from '@lang';
import { SignIn as SignInContainer } from '@routes/SignIn/containers';

import * as entityActions from '@store/entity/actions';
import { NormalizedStore } from '@store/entity/entity.types';

type SignInProps = {
  language: string;
  property: Property;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const SignIn: NextPage<SignInProps> = ({
  language,
  property,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.signin');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <SignInContainer language={language} propertyId={property.id} />
    </>
  );
};

type SignInInitialProps = {
  language: string;
  property: Property;
  store: NormalizedStore;
  url: string;
} & NextPageContext;

SignIn.getInitialProps = async ({
  store,
  property,
  language,
  url,
}: SignInInitialProps): Promise<SignInProps> => {
  if (property.homepage) {
    await entityActions.fetchHomepage(property.id, language)(store.dispatch);
  }

  return {
    language,
    property,
    propertyTitle: property.propertyTitle || '',
    socialFallbackImage: property.socialFallbackImage,
    url,
  };
};

export default SignIn;
